import { Switch } from '@advanza/advanza_generic'
import { S3_PUBLIC } from '@advanza/constants'
import { FileDropZone } from '@advanza/input'
import PopUpNotification, {
    DELIVERY_TYPE_LABELS,
    deliveryTypeIncludesPush,
    DeliveryTypes,
    DOMAIN,
} from '@advanza/interfaces/PopUpNotification'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertDialogPortal,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@advanza/shadcn-ui/src/components/ui/alertDialog'
import { Card, CardContent } from '@advanza/shadcn-ui/src/components/ui/card'
import { Input } from '@advanza/shadcn-ui/src/components/ui/input'
import { Label } from '@advanza/shadcn-ui/src/components/ui/label'
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@advanza/shadcn-ui/src/components/ui/select'
import { Button, Divider, Icon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import {
    headingsPlugin,
    linkPlugin,
    listsPlugin,
    markdownShortcutPlugin,
    quotePlugin,
} from '@mdxeditor/editor'
import { useToast } from 'components/misc/Toaster'
import style from 'components/popUpNotifications/popUpNotification.module.scss'
import { PopUpNotificationDateRangePicker } from 'components/popUpNotifications/PopUpNotificationDateRangePicker'
import PopUpNotificationFolderSelect from 'components/popUpNotifications/PopUpNotificationFolderSelect'
import MarkdownEditorWrapper from 'components/popUpNotifications/PopUpNotificationMdxEditorWrapper'
import PopUpNotificationPreviewModal from 'components/popUpNotifications/PopUpNotificationPreviewModal'
import PopUpNotificationPushFields from 'components/popUpNotifications/PopUpNotificationPushFields'
import SelectProviderTarget from 'components/popUpNotifications/SelectProviderTarget'
import ServicesSelect from 'components/services/ServicesSelect'
import { formatDistanceToNow } from 'date-fns'
import { FIELDS, PUSH_FIELDS, usePopUpNotification } from 'hooks/popUpnotificationsHooks'
import { getCompanyBaseUrlOffice } from 'misc/companyBaseUrlOffice'
import { ChangeEvent, useState } from 'react'
import { DateRange } from 'react-day-picker'

interface Props {
    data: PopUpNotification
    setTableRow: (data: PopUpNotification) => void
}

const getStatusStringValue = (status: number): string => {
    if (status === 1) return '1'
    if (status === 2) return '2'
    return '0'
}

const PopUpNotificationEntity = ({ data, setTableRow }: Props) => {
    const { onFilesRejected } = useToast()
    const { deletePopUpNotification, savePopUpNotification } = usePopUpNotification()

    const [toast, setToast] = useState({
        isError: false,
        id: Date.now(),
        message: '',
    })
    const [showNotificationUrl, setShowNotificationUrl] = useState(false)
    const [showCopiedTooltip, setShowCopiedTooltip] = useState(false)
    const [showApiUrl, setShowApiUrl] = useState(false)
    const [showApiCopiedTooltip, setShowApiCopiedTooltip] = useState(false)
    const [selectedServicesValue, setSelectedServicesValue] = useState(data[FIELDS.SERVICES_TARGET])
    const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [dataState, setDataState] = useState<PopUpNotification>(data)
    const [statusValue, setStatusValue] = useState<string>(
        getStatusStringValue(data[FIELDS.STATUS]),
    )
    const [hasCta, setHasCta] = useState(data.cta_button?.name !== undefined)
    const [hasImage, setHasImage] = useState(data[FIELDS.HEADER_IMAGE] !== null)
    const [hasLabel, setHasLabel] = useState(data[FIELDS.LABEL] !== null)
    const [showPushApiUrl, setShowPushApiUrl] = useState(false)

    const hasPush = deliveryTypeIncludesPush(dataState.delivery_type)

    const handleDataUpdate = (
        key: string,
        value: string | number | string[] | DateRange | null | undefined,
    ) => {
        if (key === FIELDS.SPECIFIC_TARGETS) {
            const targets = (value as string)
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
            setDataState((prevState) => ({ ...prevState, [key]: targets }))
        } else if (
            key === FIELDS.CTA_BUTTON.NAME ||
            key === FIELDS.CTA_BUTTON.URL ||
            key === FIELDS.CTA_BUTTON.TARGET
        ) {
            setDataState((prevState) => ({
                ...prevState,
                cta_button: {
                    name:
                        key === FIELDS.CTA_BUTTON.NAME
                            ? (value as string)
                            : prevState.cta_button?.name || '',
                    url:
                        key === FIELDS.CTA_BUTTON.URL
                            ? (value as string)
                            : prevState.cta_button?.url || '',
                    target:
                        key === FIELDS.CTA_BUTTON.TARGET
                            ? (value as string)
                            : prevState.cta_button?.target || '',
                },
            }))
        } else {
            setDataState((prevState) => ({ ...prevState, [key]: value }))
        }
        setIsSaveBtnDisabled(false)
    }

    async function onDelete() {
        try {
            if (!data.pop_up_notification_id) {
                window.location.reload()
                return
            }
            await deletePopUpNotification(data.pop_up_notification_id)
            window.location.reload()
        } catch (err) {
            setToast({ isError: true, id: Date.now(), message: JSON.stringify(err) })
        }
    }

    async function onSave() {
        try {
            setIsSaving(true)
            setIsSaveBtnDisabled(true)

            // First save to get the real pop_up_notification_id
            const updatedData = await savePopUpNotification({
                ...dataState,
                header_image: dataState.file ? undefined : dataState.header_image, // Don't send header_image if we have a new file
            })

            // If we have a new file, save it with the real pop_up_notification_id
            if (dataState.file) {
                const finalData = {
                    ...updatedData,
                    file: dataState.file,
                    header_image: dataState.file.filename,
                }
                const finalUpdatedData = await savePopUpNotification(finalData)
                setDataState(finalUpdatedData)
                setTableRow(finalUpdatedData)
            } else {
                setDataState(updatedData)
                setTableRow(updatedData)
            }

            if (!data.pop_up_notification_id) {
                window.location.reload()
            }
        } catch (err) {
            setToast({ isError: true, id: Date.now(), message: JSON.stringify(err) })
        } finally {
            setIsSaving(false)
        }
    }

    const getApiIconUrl = (data: PopUpNotification) => {
        return `${S3_PUBLIC()}provider_notifications/${data.pop_up_notification_id}/${
            dataState[FIELDS.HEADER_IMAGE]
        }`
    }

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { id, value } = event.target

        if (id === FIELDS.CTA_BUTTON.NAME || id === FIELDS.CTA_BUTTON.URL) {
            setDataState((prevState) => ({
                ...prevState,
                cta_button: {
                    name: id === FIELDS.CTA_BUTTON.NAME ? value : prevState.cta_button?.name || '',
                    url: id === FIELDS.CTA_BUTTON.URL ? value : prevState.cta_button?.url || '',
                    target: prevState.cta_button?.target || '',
                },
            }))
        } else {
            handleDataUpdate(id, value)
        }
        setIsSaveBtnDisabled(false)
    }

    const handleStatusChange = (value: string) => {
        const statusNum = parseInt(value, 10) as 0 | 1 | 2
        setStatusValue(value)
        setDataState((prevState) => ({ ...prevState, [FIELDS.STATUS]: statusNum }))
        setIsSaveBtnDisabled(false)
    }

    const getBaseUrl = () => {
        const countryCode = dataState.country_code || 'NL'
        let baseUrl = getCompanyBaseUrlOffice(countryCode)

        // For pros app, use port 3001 in local development (instead of 3000 for Next.js)
        const { hostname } = window.location
        if (
            (hostname.startsWith('localhost') || hostname.startsWith('l.')) &&
            baseUrl.includes(':3000')
        ) {
            baseUrl = baseUrl.replace(':3000', ':3001')
        }

        return baseUrl
    }

    const getApiBaseUrl = () => {
        const countryCode = dataState.country_code || 'NL'
        const hostnamePart =
            countryCode.toUpperCase() === 'NL'
                ? 'trustoo.nl'
                : `trustlocal.${countryCode.toLowerCase()}`
        const { hostname } = window.location

        if (hostname.startsWith('localhost') || hostname.startsWith('l.')) {
            return `http://l.${hostnamePart}`
        } else if (hostname.startsWith('test')) {
            return `https://test.${hostnamePart}`
        } else {
            return `https://${hostnamePart}`
        }
    }

    const getNotificationUrl = () => {
        const popupId = dataState.pop_up_notification_id
        if (!popupId) return ''

        const baseUrl = getBaseUrl()
        return `${baseUrl}/pro/dashboard?popup=${popupId}`
    }

    const getApiTriggerUrl = () => {
        const popupId = dataState.pop_up_notification_id
        if (!popupId) return ''

        const baseUrl = getApiBaseUrl()
        return `${baseUrl}/api/hubspot/trigger-pop-up-notification?popup=${popupId}`
    }

    const getPushApiTriggerUrl = () => {
        const popupId = dataState.pop_up_notification_id
        if (!popupId) return ''

        return `${getApiBaseUrl()}/api/hubspot/trigger-push-notification?popup=${popupId}`
    }

    const copyToClipboard = async (text: string) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text)
        } else {
            // Fallback for browsers without clipboard API
            const textArea = document.createElement('textarea')
            textArea.value = text
            textArea.style.position = 'fixed'
            textArea.style.opacity = '0'
            textArea.style.left = '-9999px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()
            document.execCommand('copy')
            document.body.removeChild(textArea)
        }
    }

    const copyNotificationLinkToClipboard = async () => {
        try {
            const popupId = dataState.pop_up_notification_id
            if (!popupId) {
                console.warn('No popup ID available')
                return
            }

            const url = getNotificationUrl()
            if (!url) {
                console.warn('Could not generate URL')
                return
            }

            await copyToClipboard(url)
            setShowNotificationUrl(true)
            setShowCopiedTooltip(true)
            setTimeout(() => {
                setShowCopiedTooltip(false)
            }, 2000)
        } catch (error) {
            console.error('Error copying to clipboard:', error)
        }
    }

    const copyApiTriggerUrlToClipboard = async () => {
        try {
            const popupId = dataState.pop_up_notification_id
            if (!popupId) {
                console.warn('No popup ID available')
                return
            }

            const url = getApiTriggerUrl()
            if (!url) {
                console.warn('Could not generate API URL')
                return
            }

            await copyToClipboard(url)
            setShowApiUrl(true)
            setShowApiCopiedTooltip(true)
            setTimeout(() => {
                setShowApiCopiedTooltip(false)
            }, 2000)
        } catch (error) {
            console.error('Error copying API URL to clipboard:', error)
        }
    }

    const handleProvidersSelectedChange = (value: string) => {
        handleDataUpdate(FIELDS.USER_TARGET, value)
    }

    const handleDateRangeChange = (date: DateRange | undefined) => {
        if (date === undefined) {
            setDataState((prevState) => ({ ...prevState, [FIELDS.AVAILABILITY]: undefined }))
        } else {
            handleDataUpdate(FIELDS.AVAILABILITY, date)
        }
        setIsSaveBtnDisabled(false)
    }

    const handleServiceSelectChange = (value: any) => {
        handleDataUpdate(FIELDS.SERVICES_TARGET, value)
        setSelectedServicesValue(value)
    }

    const toggleSaveBtn = (flag: boolean) => {
        setIsSaveBtnDisabled(flag)
    }

    const onDropFiles = (files: File[]) => {
        setIsSaveBtnDisabled(false)
        const reader = new FileReader()
        reader.onloadend = (e) => {
            const result = (e.target as FileReader).result as string
            setDataState((prev) => ({
                ...prev,
                file: {
                    preview: result,
                    base64: result.substring(result.indexOf(',') + 1),
                    filename: files[0].name,
                },
                [FIELDS.HEADER_IMAGE]: files[0].name,
            }))
        }
        reader.readAsDataURL(files[0])
    }

    const handleSpecificProvidersChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setDataState((prevState) => ({
            ...prevState,
            [FIELDS.SPECIFIC_TARGETS]: value
                ? value
                      .replace(/,\s*$/, '') // Remove trailing comma
                      .split(',')
                      .map((item) => item.trim())
                      .filter(Boolean)
                : [],
        }))
        setIsSaveBtnDisabled(false)
    }

    const handleTargetChange = (checked: boolean) => {
        setDataState((prevState) => ({
            ...prevState,
            cta_button: {
                name: prevState.cta_button?.name || '',
                url: prevState.cta_button?.url || '',
                target: checked ? '_blank' : '',
            },
        }))
        setIsSaveBtnDisabled(false)
    }

    return (
        <>
            {/* min-w-0 on both columns: grid items default to their content's intrinsic width,
                which let a wide field push the whole table into horizontal scroll. */}
            <div className="grid grid-cols-2 items-start gap-4">
                <Card className="w-full min-w-0 overflow-hidden">
                    <CardContent>
                        <div className="grid gap-4">
                            <div>
                                {hasImage ? (
                                    <div className="grid w-full items-center gap-1.5 bg-grey">
                                        <FileDropZone
                                            allowedTypes={[
                                                'image/jpeg',
                                                'image/png',
                                                'image/gif',
                                                'image/svg+xml',
                                            ]}
                                            allowSvg
                                            multiple={false}
                                            maxFiles={1}
                                            onFilesAccepted={onDropFiles}
                                            onFilesRejected={onFilesRejected}>
                                            <div
                                                style={{
                                                    background: dataState.file?.preview
                                                        ? `url(${dataState.file.preview}) no-repeat center center /contain`
                                                        : dataState[FIELDS.HEADER_IMAGE]
                                                          ? `url(${getApiIconUrl(
                                                                data,
                                                            )}) no-repeat center center /contain`
                                                          : 'none',
                                                    border: '3px dotted #dadada',
                                                    padding: 6,
                                                    height: 150,
                                                }}>
                                                {!dataState.file?.preview &&
                                                    !dataState[FIELDS.HEADER_IMAGE] &&
                                                    'Click to add image'}
                                            </div>
                                        </FileDropZone>
                                        <span
                                            onClick={() => {
                                                handleDataUpdate(FIELDS.HEADER_IMAGE, '')
                                                setHasImage(false)
                                            }}>
                                            remove image
                                        </span>
                                    </div>
                                ) : (
                                    <Button name="text" onClick={() => setHasImage(true)}>
                                        <Icon name="add" /> add image
                                    </Button>
                                )}
                            </div>
                            <div className="grid w-full items-start gap-1.5 bg-grey">
                                <Label htmlFor={FIELDS.TITLE}>Title</Label>
                                <Input
                                    className="w-full bg-white border border-blue-500 rounded-lg px-4 py-2 text-neutral-800 focus:border-0 focus:border-blue-700 focus:ring-1 focus:ring-blue-500"
                                    onChange={handleInputChange}
                                    type="text"
                                    id={FIELDS.TITLE}
                                    placeholder="Title"
                                    value={dataState.title}
                                />
                            </div>
                            <div className="bg-grey">
                                {hasLabel ? (
                                    <>
                                        <Label htmlFor={FIELDS.LABEL}>Label</Label>
                                        <Input
                                            className="w-full bg-white border border-blue-500 rounded-lg px-4 py-2 text-neutral-800 focus:border-0 focus:border-blue-700 focus:ring-1 focus:ring-blue-500"
                                            onChange={handleInputChange}
                                            type="text"
                                            id={FIELDS.LABEL}
                                            placeholder="Label"
                                            value={dataState.label}
                                        />
                                        <span
                                            onClick={() => {
                                                handleDataUpdate(FIELDS.LABEL, '')
                                                setHasLabel(false)
                                            }}>
                                            remove
                                        </span>
                                    </>
                                ) : (
                                    <Button name="text" onClick={() => setHasLabel(true)}>
                                        <Icon name="add" /> add label
                                    </Button>
                                )}
                            </div>
                            <Divider m />
                            <div className="grid w-full items-center gap-1.5 bg-white">
                                <MarkdownEditorWrapper
                                    placeholder={'Add content'}
                                    className={style.editor}
                                    onChange={(newMarkdown) => {
                                        setDataState((prevState) => ({
                                            ...prevState,
                                            content: newMarkdown,
                                        }))
                                        toggleSaveBtn(false)
                                    }}
                                    markdown={String(dataState.content)}
                                    plugins={[
                                        headingsPlugin(),
                                        listsPlugin(),
                                        linkPlugin(),
                                        quotePlugin(),
                                        markdownShortcutPlugin(),
                                    ]}
                                />
                            </div>
                            <Divider m />
                            <div className="w-full items-center gap-1.5 bg-grey ">
                                {hasCta ? (
                                    <>
                                        <Label htmlFor={FIELDS.CTA_BUTTON.NAME}>Cta button</Label>
                                        <div className="grid gap-1">
                                            <Input
                                                className="w-full bg-white border border-blue-500 rounded-lg px-4 py-2 text-neutral-800 focus:border-0 focus:border-blue-700 focus:ring-1 focus:ring-blue-500"
                                                onChange={handleInputChange}
                                                type="text"
                                                id={FIELDS.CTA_BUTTON.NAME}
                                                placeholder="Button Name"
                                                value={dataState.cta_button?.name || ''}
                                            />
                                            <Input
                                                className="w-full bg-white border border-blue-500 rounded-lg px-4 py-2 text-neutral-800 focus:border-0 focus:border-blue-700 focus:ring-1 focus:ring-blue-500"
                                                onChange={handleInputChange}
                                                type="text"
                                                id={FIELDS.CTA_BUTTON.URL}
                                                placeholder={`Always full url (eg. https://${DOMAIN}/pro/dashboard)`}
                                                value={dataState.cta_button?.url || ''}
                                            />
                                            <div className="flex items-center space-x-2 mt-2">
                                                <Switch
                                                    id="target"
                                                    checked={
                                                        dataState.cta_button?.target === '_blank'
                                                    }
                                                    onCheckedChange={(checked: boolean) => {
                                                        handleTargetChange(checked)
                                                    }}
                                                />
                                                <Label htmlFor="target">
                                                    Open in{' '}
                                                    <span className="font-bold underline">
                                                        {dataState.cta_button?.target === '_blank'
                                                            ? 'New'
                                                            : 'Current'}
                                                    </span>{' '}
                                                    Tab
                                                </Label>
                                            </div>
                                            <div>
                                                <br />
                                                <div className="text-sm text-neutral-600">
                                                    <p>
                                                        <span className="font-bold">
                                                            Always use full url, as follows:
                                                        </span>
                                                        <ul className="list-disc list-outside ml-5 marker:text-neutral-400">
                                                            <li>https://{DOMAIN}/pro/dashboard</li>
                                                            <li>
                                                                https://www.{DOMAIN}/pro/dashboard
                                                            </li>
                                                            <li>https://www.google.com</li>
                                                        </ul>
                                                    </p>
                                                    <p>
                                                        <span className="font-bold">
                                                            You can also add placeholders:
                                                        </span>
                                                        <ul className="list-disc list-outside ml-5 marker:text-neutral-400 text-wrap">
                                                            <li className="break-all">
                                                                {
                                                                    'https://platform.example.com/action?providerId={{providerId}}&serviceId={{serviceId}}&user={{username}}'
                                                                }
                                                            </li>
                                                        </ul>
                                                    </p>
                                                </div>
                                                <br />
                                                <span
                                                    onClick={() => {
                                                        setDataState((prev) => ({
                                                            ...prev,
                                                            cta_button: {
                                                                name: '',
                                                                url: '',
                                                                target: '',
                                                            },
                                                        }))
                                                        setHasCta(false)
                                                    }}
                                                    className="cursor-pointer text-blue-600 hover:text-blue-800 underline">
                                                    remove custom cta
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <Button
                                        name="text"
                                        onClick={() => {
                                            setHasCta(true)

                                            setDataState((prev) => ({
                                                ...prev,
                                                cta_button: { name: '', url: '', target: '' },
                                            }))
                                        }}>
                                        <Icon name="add" /> add custom cta
                                    </Button>
                                )}
                            </div>
                            {hasPush && (
                                <>
                                    <Divider m />
                                    <PopUpNotificationPushFields
                                        data={dataState}
                                        onChange={handleDataUpdate}
                                    />
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>
                <div className="grid min-w-0 p-4 gap-6">
                    <div className="grid gap-2">
                        <div className="grid w-full max-w-sm items-center ">
                            <Label htmlFor={FIELDS.SERVICES_TARGET}>Services</Label>
                        </div>
                        <ServicesSelect
                            id={FIELDS.SERVICES_TARGET}
                            useSelectedCountry
                            multiple
                            onChangeValue={handleServiceSelectChange}
                            value={selectedServicesValue}
                        />
                    </div>
                    <div className="grid gap-1.5 bgWhite">
                        <div className="grid w-full max-w-sm items-center ">
                            <Label htmlFor="services">Providers</Label>
                        </div>

                        <SelectProviderTarget
                            onChangeValue={handleProvidersSelectedChange}
                            onChangeSpecificProviders={handleSpecificProvidersChange}
                            value={dataState.users_target}
                            specificProviders={dataState.specific_targets}
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <div className="grid w-full max-w-sm items-center ">
                            <Label htmlFor="services">Activation period</Label>
                        </div>
                        <PopUpNotificationDateRangePicker
                            onChangeValue={handleDateRangeChange}
                            value={dataState.availability}
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor={FIELDS.DELIVERY_TYPE}>Delivery type</Label>
                        <Select
                            value={dataState.delivery_type ?? DeliveryTypes.POP_UP}
                            onValueChange={(value) =>
                                handleDataUpdate(FIELDS.DELIVERY_TYPE, value)
                            }>
                            <SelectTrigger className="w-full border border-neutral-200 bg-white">
                                <SelectValue placeholder="Select delivery type" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectGroup>
                                    {Object.values(DeliveryTypes).map((deliveryType) => (
                                        <SelectItem key={deliveryType} value={deliveryType}>
                                            {DELIVERY_TYPE_LABELS[deliveryType]}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor={PUSH_FIELDS.FOLDER_ID}>Folder</Label>
                        <PopUpNotificationFolderSelect
                            value={dataState.pop_up_notification_folder_id}
                            onChangeValue={(folderId) =>
                                handleDataUpdate(PUSH_FIELDS.FOLDER_ID, folderId)
                            }
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="status">Status</Label>
                        <Select value={statusValue} onValueChange={handleStatusChange}>
                            <SelectTrigger className="w-full border border-neutral-200 bg-white">
                                <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                <SelectGroup>
                                    <SelectItem value="1">Active</SelectItem>
                                    <SelectItem value="0">Inactive</SelectItem>
                                    <SelectItem value="2">Triggerable</SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                        {dataState.pop_up_notification_id && (
                            <div className="mt-2 space-y-2">
                                {showNotificationUrl ? (
                                    <div className="p-2 bg-neutral-50 border border-neutral-200 rounded transition-opacity duration-300 ease-in-out">
                                        <div
                                            className="font-mono text-sm text-neutral-700 break-all cursor-pointer hover:text-blue-600"
                                            onClick={(e) => {
                                                const selection = window.getSelection()
                                                const range = document.createRange()
                                                range.selectNodeContents(e.currentTarget)
                                                selection?.removeAllRanges()
                                                selection?.addRange(range)
                                            }}>
                                            {getNotificationUrl()}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                copyNotificationLinkToClipboard()
                                            }}
                                            style={{
                                                backgroundColor: '#ffffff',
                                                color: '#3b82f6',
                                                borderColor: '#3b82f6',
                                            }}
                                            className="w-full px-4 py-2 text-md font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-opacity duration-300 ease-in-out"
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#eff6ff'
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#ffffff'
                                            }}>
                                            Get notification link
                                        </button>
                                        {showCopiedTooltip && (
                                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 text-sm font-medium text-white bg-neutral-800 rounded shadow-lg transition-opacity duration-300 ease-in-out animate-in fade-in">
                                                Copied
                                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                                    <div className="border-4 border-transparent border-t-neutral-800"></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {statusValue === '2' && hasPush && dataState.pop_up_notification_id && (
                            <div className="mt-2 space-y-2">
                                {showPushApiUrl ? (
                                    <div className="p-2 bg-neutral-50 border border-neutral-200 rounded">
                                        <div className="font-mono text-sm text-neutral-700 break-all">
                                            {getPushApiTriggerUrl()}
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            void copyToClipboard(getPushApiTriggerUrl()).then(() =>
                                                setShowPushApiUrl(true),
                                            )
                                        }}
                                        className="w-full px-4 py-2 text-md font-medium rounded-md border border-orange-300 bg-white text-orange-300 hover:bg-orange-50">
                                        Get push trigger URL
                                    </button>
                                )}
                            </div>
                        )}
                        {statusValue === '2' && dataState.pop_up_notification_id && (
                            <div className="mt-2 space-y-2">
                                {showApiUrl ? (
                                    <div className="p-2 bg-neutral-50 border border-neutral-200 rounded transition-opacity duration-300 ease-in-out">
                                        <div
                                            className="font-mono text-sm text-neutral-700 break-all cursor-pointer hover:text-blue-600"
                                            onClick={(e) => {
                                                const selection = window.getSelection()
                                                const range = document.createRange()
                                                range.selectNodeContents(e.currentTarget)
                                                selection?.removeAllRanges()
                                                selection?.addRange(range)
                                            }}>
                                            {getApiTriggerUrl()}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                copyApiTriggerUrlToClipboard()
                                            }}
                                            style={{
                                                backgroundColor: '#ffffff',
                                                color: '#fb923c',
                                                borderColor: '#fb923c',
                                            }}
                                            className="w-full px-4 py-2 text-md font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-300 transition-opacity duration-300 ease-in-out"
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#fff7ed'
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#ffffff'
                                            }}>
                                            Get trigger URL
                                        </button>
                                        {showApiCopiedTooltip && (
                                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 text-sm font-medium text-white bg-neutral-800 rounded shadow-lg transition-opacity duration-300 ease-in-out animate-in fade-in">
                                                Copied
                                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                                    <div className="border-4 border-transparent border-t-neutral-800"></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Divider m />
            <div className="flex justify-between">
                <div className="flex items-center gap-2">
                    <Button onClick={onSave} disabled={isSaveBtnDisabled}>
                        {isSaving ? 'Saving' : 'Save'}
                    </Button>
                    <Button
                        appearance="outlined_primary"
                        onClick={() =>
                            void NiceModal.show(PopUpNotificationPreviewModal, {
                                notification: dataState,
                            })
                        }>
                        Show test notification
                    </Button>
                    {toast.isError && <div style={{ color: 'red' }}>not saved</div>}
                    {dataState.modified && (
                        <small className="c-grey">
                            Modified{' '}
                            {formatDistanceToNow(new Date(dataState.modified), { addSuffix: true })}
                        </small>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {dataState.created && (
                        <small className="c-grey">
                            Created{' '}
                            {formatDistanceToNow(new Date(dataState.created), { addSuffix: true })}
                        </small>
                    )}
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button appearance="outlined_primary">Delete</Button>
                        </AlertDialogTrigger>
                        <AlertDialogPortal>
                            <AlertDialogOverlay className="bg-black/50 backdrop-blur-sm fixed inset-0 z-50" />
                            <AlertDialogContent className="bg-white">
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This action cannot be undone
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialogPortal>
                    </AlertDialog>
                </div>
            </div>
        </>
    )
}

export default PopUpNotificationEntity
